// Feature spec 17, phase 3: the Entry points tab's list, form and dialogs, the Overview band and the deployment side
// (spec 11 6.15.5, 6.15.10, 6.15.28–30).
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const usd = (currency: string, n: number) => `${currency} ${n.toLocaleString('en-US')}`;

export const networkEntryPoints = {
  // 6.15.10 List
  summary: (eps: number, routes: number) => `${plural(eps, 'entry point')} · ${plural(routes, 'route')}`,
  kindAndTier: (kind: string, tier: string | null) => (tier ? `${kind} · ${tier}` : kind),
  regionHub: (region: string) => `${region} hub`,
  notApplied: 'Not applied yet',
  changed: 'Changed since the last apply',
  copyHost: 'Copy host name',
  copyIp: 'Copy public IP',
  actions: (id: string) => `Actions for entry point ${id}`,
  edit: 'Edit…',
  rotate: 'Rotate origin secret…',
  remove: 'Remove…',
  refresh: 'Refresh status',
  expand: (id: string) => `Show entry point ${id}`,
  collapse: (id: string) => `Hide entry point ${id}`,
  statusUnknown: 'Status unknown',
  chips: {
    wafPrevention: 'WAF prevention',
    wafDetection: 'WAF detection',
    wafDetectionHint: 'WAF in detection mode: requests aren’t blocked',
    managedRules: 'managed rules',
    cdnOff: 'CDN off',
    cdnOn: 'CDN caching',
    privateOrigins: 'Private origins',
    ddosIp: 'DDoS IP Protection',
    ddosNetwork: 'DDoS Network Protection',
  },
  domains: 'Domains',
  domainsCaption: (id: string) => `Domains of entry point ${id}`,
  hostName: 'Host name',
  state: 'State',
  next: 'What to do',
  domainState: {
    validated: 'Validated',
    'waiting-for-dns': 'Waiting for DNS',
    pending: 'Pending (not applied)',
    failed: 'Validation failed',
    unknown: 'Unknown',
  } as Record<string, string>,
  managedCertificate: 'managed certificate',
  keyVaultCertificate: 'Key Vault certificate',
  renews: 'renews automatically',
  lastKnown: (time: string) => `last known ${time}`,
  createVerb: 'Create',
  copyRecord: (type: string, name: string) => `Copy the ${type} record of ${name}`,
  appliesFirst: 'Shown after the first apply',
  routes: 'Routes',
  routesCaption: (id: string) => `Routes of entry point ${id}`,
  hostAndPath: 'Host and path',
  origin: 'Origin',
  originAccess: 'Origin access',
  health: 'Health',
  edgeHost: '(edge host name)',
  allHosts: 'all domains',
  noRoutes: 'No routes yet: the edge answers nothing until a route sends requests to an app.',
  healthWords: {
    healthy: 'Healthy',
    unhealthy: 'Unhealthy',
    'not-probed': 'Not probed',
    unknown: 'Unknown',
  } as Record<string, string>,
  notProbedHint: 'Front Door doesn’t probe single-origin groups',
  waitingForApply: 'Waiting for apply',
  notAppliedRoute: 'Not applied',
  inactive: (reason: string) => `Inactive: ${reason}`,
  crossCloud: { aws: 'AWS', gcp: 'Google Cloud' } as Record<string, string>,
  pendingOrigins: (names: string) =>
    `Waiting for ${names} to be applied: its app isn’t locked to this edge yet.`,
  certificateAccess: (vault: string, principal: string, network: string) =>
    `Grant Key Vault Secrets User on ${vault} to the gateway’s identity ${principal}, then apply ${network}.`,
  copyPrincipal: 'Copy the identity’s principal ID',
  rotation: (route: string) => `Rotating the origin secret of route ${route}:`,
  rotationStep: {
    requested: (network: string) => `waits for ${network}’s next apply`,
    delivering: 'waiting for',
    deliveringAfter: 'to be applied',
    switching: (network: string) => `waits for ${network}’s next apply, which switches the edge`,
    dropping: (network: string) => `the old secret is removed with ${network}’s next apply`,
  },
  stale: (time: string) => `The status couldn’t be read; these are the last known states from ${time}.`,
  appGatewayHub: (region: string) => `Azure Application Gateway · WAF_v2 · ${region} hub`,
  // 6.15.10 Form
  newTitle: (kind: string) => `New entry point · ${kind}`,
  editTitle: (id: string) => `Edit entry point ${id}`,
  name: 'Name',
  id: 'ID',
  idHelp: 'Lowercase letters, digits and hyphens; fixed once saved.',
  idInvalid: 'Use lowercase letters, digits and hyphens, starting with a letter (at most 31).',
  idTaken: (id: string) => `Another entry point is called ${id}.`,
  label: 'Label (optional)',
  tier: 'Tier',
  tierCost: (label: string, currency: string, monthly: number) =>
    `${label} · ${usd(currency, monthly)}/month`,
  premiumExtras: 'managed WAF rules, bot protection, Private Link origins',
  region: 'Region',
  regionHelp: 'The gateway sits in this region’s hub; routes reach apps attached in this region.',
  addOns: 'Add-ons',
  addOnCost: (currency: string, monthly: number) => `${usd(currency, monthly)}/month`,
  perPublicIp: 'per public IP',
  needsTier: (tier: string) => `Needs ${tier}`,
  wafMode: 'Mode',
  prevention: 'Prevention',
  detection: 'Detection',
  detectionHelp: 'Requests aren’t blocked; use it for a first rollout.',
  managedRules: 'Managed rules',
  botProtection: 'Bot protection',
  managedNeedsPremium: 'Managed rules and bot protection need Premium.',
  rateLimit: 'Rate limit per client IP and minute',
  rateLimitHelp: '0 turns the rate limit off; otherwise 100 to 100,000.',
  rateLimitInvalid: 'Enter 0 or a number from 100 to 100,000.',
  ddosExclusive: 'DDoS IP Protection and DDoS Network Protection exclude each other.',
  domainsHelp: 'Host names customers use; each needs a certificate.',
  domainHost: (i: number) => `Host name of domain ${i}`,
  domainCertificate: (i: number) => `Certificate of domain ${i}`,
  secretId: (i: number) => `Key Vault secret ID of domain ${i}`,
  managed: 'Managed',
  keyVault: 'Key Vault',
  keyVaultOnly:
    'The gateway reads the certificate with its own identity; after the first apply the entry point shows the identity to grant Key Vault Secrets User.',
  addDomain: 'Add domain',
  removeDomain: (host: string) => `Remove domain ${host || 'without a name'}`,
  hostInvalid: 'Enter a host name such as shop.example.com.',
  hostDuplicate: 'This host name is used twice.',
  secretIdRequired: 'Enter the certificate’s Key Vault secret ID.',
  routesHelp: 'Each route sends requests for its hosts and paths to one app.',
  route: (i: number) => `Route ${i}`,
  routeId: (i: number) => `ID of route ${i}`,
  routeHosts: (i: number) => `Host names of route ${i}`,
  allDomains: 'All domains',
  edgeOwnHost: 'The edge’s own host name',
  routePaths: (i: number) => `Paths of route ${i}`,
  pathsHelp: 'Comma-separated; /* for everything, /api/* for a prefix, or an exact path.',
  pathInvalid: 'Paths start with / and only end in * after a /.',
  routeOrigin: (i: number) => `Origin of route ${i}`,
  chooseOrigin: 'Choose an app…',
  noOrigins: (network: string) => `Attach a deployment to ${network} to route to its apps.`,
  originMissing: 'Choose an app.',
  inRegion: (region: string) => `Apps attached in ${region}`,
  routeAccess: (i: number) => `Origin access of route ${i}`,
  notLocked: 'Not locked',
  strength: {
    strongest: 'strongest',
    strong: 'strong',
    medium: 'medium',
    weak: 'weak',
    none: 'none',
  } as Record<string, string>,
  accessLabel: {
    'private-link': 'Private Link',
    'private-endpoint': 'Private endpoint',
    'vpc-origin': 'VPC origin',
    'serverless-neg': 'Serverless NEG',
    'access-restriction': 'Access restriction',
    'allow-list-and-header': 'Edge ranges and secret header',
    header: 'Secret header',
    none: 'Not locked',
  } as Record<string, string>,
  accessSentence: {
    'private-link':
      'Private Link: the app accepts only this Front Door, privately; the app’s next apply approves the connection.',
    'private-endpoint': 'Private endpoint: the gateway reaches the app privately through the hub.',
    'vpc-origin': 'VPC origin: the edge reaches the app privately.',
    'serverless-neg': 'Serverless NEG: the load balancer reaches the service directly.',
    'access-restriction':
      'Access restriction: the app accepts only Front Door’s service tag with this Front Door’s ID.',
    'allow-list-and-header':
      'The edge’s address ranges plus a secret header: the app must check X-Nebisys-Origin against NEBISYS_ORIGIN_SECRET.',
    header: 'A secret header only: the app must check X-Nebisys-Origin against NEBISYS_ORIGIN_SECRET.',
    none: 'Not locked: callers can reach the app directly and bypass the WAF.',
  } as Record<string, string>,
  mustChoose:
    'No lock-down is preselected for this platform: choose one, knowing what the weaker ones allow.',
  removeRoute: (id: string) => `Remove route ${id || 'without an ID'}`,
  addRoute: 'Add route',
  push: 'Keep Event Grid and SignalR pushes',
  pushHelp: (app: string) =>
    `Every route of ${app} becomes reachable from any customer’s Event Grid or SignalR service without the WAF; separate function apps for push triggers and edge routes avoid this.`,
  costs: 'Costs',
  costItem: (item: string, currency: string, monthly: number) => `${item} ${usd(currency, monthly)}/month`,
  costLine: (items: string, asOf: string) =>
    `Fixed costs: ${items} (list price, as of ${asOf}, without traffic)`,
  noFixedCost: 'No fixed cost',
  costsPending: 'Costs follow the check of your changes.',
  checking: 'Checking…',
  impactUnknown: 'Impact unknown: the check failed.',
  save: 'Save',
  saving: 'Saving…',
  saved: (network: string, id: string) => `Saved. Apply ${network} to create ${id}.`,
  savedEdit: (network: string, id: string) => `Saved. Apply ${network} to update ${id}.`,
  conflict:
    'Someone else changed this network’s entry points; your entry point was merged into theirs. Check it and save again.',
  fixErrors: (n: number) => `${plural(n, 'error')} to fix before saving.`,
  // Remove
  removeTitle: (id: string) => `Remove entry point ${id}?`,
  removeBody: (id: string) => `Traffic through entry point ${id} stops with the network’s next apply.`,
  removeOrigins: (names: string) =>
    `Its origins (${names}) keep their lock-down until their next apply, so they stay unreachable directly.`,
  removeConfirm: 'Remove entry point',
  removed: (network: string, id: string) => `Removed ${id}. Apply ${network} to delete it in the cloud.`,
  // Rotate
  rotateTitle: (id: string) => `Rotate the origin secret of ${id}`,
  rotateRoute: 'Route',
  allRoutes: 'All routes with a secret',
  rotateSteps: (network: string) => [
    `${network}’s next apply adds a new secret while the edge keeps sending the current one.`,
    'Each origin deployment’s next apply accepts both.',
    `${network}’s next apply then switches the edge; the old secret is removed afterwards.`,
  ],
  rotateNoValue: 'No secret value is ever shown or copied.',
  startRotation: 'Start rotation',
  rotationStarted: 'Rotation requested.',
  noSecretRoutes: 'No route of this entry point uses an origin secret.',
  // 6.15.5 Overview
  band: 'Entry points',
  bandNone: 'No entry points.',
  bandCard: (id: string, kind: string, tier: string | null, waf: string) =>
    [id, kind + (tier ? ` ${tier}` : ''), waf].filter(Boolean).join(', '),
  pendingCount: (n: number) => `${plural(n, 'origin')} waiting for apply`,
  treeItem: (id: string, kind: string, waf: string, routes: number, pending: number) =>
    [
      id,
      kind,
      waf,
      plural(routes, 'route'),
      pending ? `${plural(pending, 'origin')} waiting for apply` : null,
    ]
      .filter(Boolean)
      .join(', '),
  wafWords: (mode: string | null) =>
    mode === 'detection' ? 'WAF detection' : mode === 'prevention' ? 'WAF prevention' : 'no WAF',
  entryRoutes: (n: number) => (n === 1 ? '1 entry route' : `${n} entry routes`),
  entryRouteLine: (route: string, ep: string, resource: string, access: string, state: string) =>
    `${route} (${ep}) → ${resource} · ${access} · ${state}`,
  // 6.15.28 Deployment side
  cardTitle: 'Entry routes',
  originState: {
    applied: 'applied',
    'waiting-for-apply': 'waiting for apply',
    inactive: 'inactive',
    'not-applied': 'not applied yet',
  } as Record<string, string>,
  applyToLock: (deployment: string, resource: string) =>
    `Apply ${deployment} to lock ${resource} to its edge`,
  designerBadge: (abbr: string, ep: string, access: string) => `${abbr} ${ep} · ${access}`,
  originSecretRow: 'Entry point origin secret',
  activityKinds: {
    'network-origin-rotation-requested': 'Origin secret rotation requested',
    'network-origin-switched': 'Origin secret switched',
    'network-origin-regenerated': 'Origin secret replaced',
    'network-commitment-confirmed': 'Commitment confirmed',
  } as Record<string, string>,
  activity: {
    requested: 'requested a rotation of the origin secret of route',
    switched: (network: string) => `${network}’s apply switched route`,
    toNew: 'to a new origin secret',
    regenerated: (network: string) => `${network}’s apply replaced a lost origin secret of route`,
    // Feature spec 17, phase 5: "Rigmor confirmed AWS Shield Advanced: USD 3,000/month for 12 months".
    confirmed: 'confirmed',
    confirmedRenewal: 'confirmed the automatic renewal of',
    commitmentTerms: (currency: string, monthly: number, months: number) =>
      `: ${usd(currency, monthly)}/month for ${months} months`,
    forEntryPoints: (ids: string) => `(${ids})`,
  },
  // 6.15.33 AWS entry points (feature spec 17, phase 5; mocks n05, n05b, n05c)
  aws: {
    heldReason:
      'Coming later: Global Accelerator passes TLS through to the ECS Express load balancer, which can’t present a certificate for your own domain.',
    usageBased: 'usage-based',
    fixedFee: (currency: string, monthly: number) => `fixed fee ${usd(currency, monthly)}/month`,
    commitmentChip: (label: string, years: number) => `${label} · ${years}-year`,
    pricing: 'Pricing',
    kindCost: (kind: string, currency: string, monthly: number) =>
      `${kind} · ${usd(currency, monthly)}/month, list price, without traffic`,
    usageCost: 'Usage-based: requests and data transfer',
    commitmentBadge: (years: number) => `${years}-year commitment`,
    commitmentTerms: (currency: string, monthly: number, months: number, per: string) =>
      `${usd(currency, monthly)}/month for ${months} months, per ${per}`,
    botControl: 'Bot Control',
    botControlNote: 'priced per request',
    wafUsage: 'plus requests (per web ACL, rule and request)',
    shieldOff: (endsAt: string) =>
      `Turning it off removes the protections; the subscription and its billing continue until ${endsAt}.`,
    shieldOffNoDate:
      'Turning it off removes the protections; the subscription and its billing continue until its term ends.',
    autoRenew: 'Renew automatically',
    autoRenewHelp: (months: number) =>
      `Off: the subscription ends after ${months} months and a new term needs a new confirmation.`,
    renewsOn: (date: string) => `Renews on ${date} for another 12 months.`,
    renewsAfterTerm: 'Renews when its first 12 months end.',
    endsOn: (date: string) => `Ends on ${date}; doesn’t renew.`,
    endsAfterTerm: 'Ends after 12 months; doesn’t renew.',
    alreadySubscribed: (ep: string) => `Already subscribed through entry point ${ep}: no new commitment.`,
    // Commitment dialog (round 2 U2)
    dialogTitle: (network: string, label: string) => `Subscribe ${network} to ${label}?`,
    renewTitle: (network: string, label: string) => `Renew ${label} of ${network} automatically?`,
    bills: 'AWS bills',
    perMonthFor: (currency: string, monthly: number, months: number) =>
      `${usd(currency, monthly)} per month for ${months} months`,
    forPayer: 'for the payer account of',
    listPrice: (asOf: string) => `(list price, as of ${asOf}), plus data transfer.`,
    keepsBilling: (months: number, network: string) =>
      `AWS keeps billing for the full ${months} months, also if you disable the add-on later or delete ${network}.`,
    startsOnApply: 'The subscription starts when the network is applied.',
    renewBody: (network: string, label: string, currency: string, monthly: number, months: number) =>
      `Renews ${network}’s ${label} for another ${months} months at ${usd(currency, monthly)}/month at each term’s end.`,
    renewOff: (date: string | null) =>
      `Each renewal is a new commitment${date ? `; the current term ends on ${date}` : ''}. You can turn renewal off again without a confirmation.`,
    typeToConfirm: (expected: string) => `Type ${expected} to subscribe`,
    typeToRenew: (expected: string) => `Type ${expected} to renew`,
    typeBefore: 'Type',
    typeAfter: 'to subscribe',
    typeAfterRenew: 'to renew',
    mismatch: (expected: string) => `Type ${expected} exactly.`,
    subscribe: 'Subscribe on apply',
    renew: 'Renew automatically',
    staysOff: (label: string) => `${label} stays off until confirmed`,
    costsConfirming: (items: string | null, label: string) =>
      `Fixed costs: ${items ?? 'none yet'} (${label} stays off until confirmed)`,
    commitmentLine: (label: string, currency: string, monthly: number, months: number) =>
      `Commitment: ${label} ${usd(currency, monthly)}/month for ${months} months`,
    // Domains and routes
    dnsZone: (i: number) => `Route 53 zone of domain ${i} (optional)`,
    dnsZoneHelp: 'Nebisys writes the validation records there.',
    dnsZoneInvalid: 'Enter a Route 53 hosted zone ID such as Z0123456789ABC.',
    pathsOnly: 'All domains of this entry point',
    pathsOnlyHelp: 'CloudFront routes by path: every route serves all domains.',
    passthrough: 'Global Accelerator passes TLS through to the app: clients use its host name or static IPs.',
    regionRoute: (region: string) => `Region ${region} · path /*`,
    accessLabel: {
      'vpc-origin': 'VPC origin',
      'allow-list-and-header': 'CloudFront ranges + secret header',
    } as Record<string, string>,
    accessSentence: {
      'vpc-origin':
        'VPC origin: CloudFront reaches the app privately through an internal load balancer; its tasks move to private subnets.',
      'allow-list-and-header':
        'CloudFront’s address ranges plus a secret header: the app must check X-Nebisys-Origin against NEBISYS_ORIGIN_SECRET.',
    } as Record<string, string>,
    needsVpcOrigins: 'Needs the VPC origins add-on',
    switchToNat: 'Switch Outbound internet to NAT gateways',
    // Status
    staticIps: 'Static IPs',
    copyStaticIp: (ip: string) => `Copy static IP ${ip}`,
    validatedApply: (network: string) => `Validated · apply ${network} to serve it`,
    recordsByNebisys: 'Nebisys writes the validation records into its Route 53 zone',
    shieldChip: 'Shield Advanced · 1-year commitment',
    shieldState: {
      'waiting-for-apply': 'Starts with the next apply',
      active: (date: string) => `Active until ${date}`,
      renews: (date: string) => `Active, renews ${date}`,
      'active-elsewhere': (network: string) => `Active (subscribed outside ${network})`,
    },
    renews: 'renews',
    noRenewal: 'doesn’t renew',
    // Settings tab, AWS provider block (read-only; changed on the Shield add-on in Entry points).
    settingsShieldRenews: (date: string | null) =>
      `AWS Shield Advanced renews automatically${date ? ` on ${date}` : ' when its 12 months end'}.`,
    settingsShieldEnds: (date: string | null) =>
      `AWS Shield Advanced doesn’t renew${date ? `; it ends on ${date}` : '; it ends after 12 months'}.`,
    settingsShieldChange: 'Change it in Entry points',
    waitingVpcOrigin: (deployment: string) =>
      `Waiting for ${deployment} to be applied, then for a network apply`,
    waitingNetworkApply: (network: string) => `Waiting for ${network} to be applied`,
  },
  // 6.15.37 GCP entry points (feature spec 17, phase 7; mocks n05, n05b, n05c)
  gcp: {
    kindCostLabel: 'Google Cloud load balancer',
    armorCost: (currency: string, monthly: number) => `${usd(currency, monthly)}/month per origin policy`,
    managedRules: 'Managed rules (SQL injection and cross-site scripting)',
    botLater: 'Bot management comes later',
    cdnHelp: 'Caches responses at Google’s edge.',
    // The registry's reason (spec 18 5.10 add-on `deferred`); shown when the API sends none.
    enterpriseHeld: 'Waiting for a decision on how Enterprise is offered.',
    billingNote:
      'The subscription and its renewal belong to the billing account: a billing administrator starts it in the Google Cloud console, and Nebisys enrols this network’s projects.',
    bills: 'Google Cloud bills',
    forBilling: 'for the billing account of',
    keepsBilling: (months: number) =>
      `The billing account keeps paying for the full ${months} months, also if you disable the add-on later or delete the network.`,
    startsOnApply: 'Nebisys enrols the projects when the network is applied.',
    enrols: (network: string, label: string) =>
      `Turning on ${label} enrols ${network}’s projects in the Google Cloud billing account’s subscription:`,
    enrolsTerms: (per: string, asOf: string) =>
      `(${per}; list price, as of ${asOf}), which a billing administrator starts in the Google Cloud console.`,
    domainRequired: 'The load balancer has an IP address, no host name of its own: add at least one domain.',
    managedOnly: 'Google-managed certificates (Certificate Manager); you create the authorization records.',
    secretHeaderOnly: 'Secret header only',
    // Status
    ipAddress: 'IP address',
    copyIp: (ip: string) => `Copy IP address ${ip}`,
    aRecord: 'Point the domain at the load balancer',
    authorization: 'Certificate authorization',
    enterpriseChip: 'Cloud Armor Enterprise Annual · 1-year commitment',
    enterpriseState: {
      'waiting-for-apply': 'Starts with the next apply',
      active: 'Active (billing account subscription)',
    } as Record<string, string>,
    // Deployment side
    ingressChange: (app: string) =>
      `${app} accepts only the Google Cloud load balancer; its own URL stops answering.`,
  },
  // 6.15.39 Cloudflare entry points (feature spec 17, phase 8; mocks n05, n05b, n05c)
  cloudflare: {
    pickerCost: 'From USD 0/month: your zone’s Cloudflare plan',
    plan: 'Plan',
    planHelp: 'The plan your zones are on; Nebisys doesn’t change it.',
    perZone: 'per zone',
    priceOnRequest: 'Price on request',
    planCost: (label: string, currency: string, monthly: number) =>
      monthly === 0 ? `${label} · ${usd(currency, 0)}` : `${label} · ${usd(currency, monthly)}/month`,
    waf: 'WAF',
    managedRulesFree:
      'The Free plan runs the Cloudflare Free Managed Ruleset; Pro and higher run the Cloudflare Managed Ruleset.',
    managedRulesPaid: 'Runs the Cloudflare Managed Ruleset.',
    detectionNeedsEnterprise: 'Detection mode needs Enterprise',
    // User decision 2026-11-04 (spec 11 item 39).
    rateLimitFree:
      "Rate limiting needs the Pro plan or higher: on Free, Cloudflare's rate limiting rules can't match host names.",
    botZoneSetting: 'Bot protection is a zone setting; Nebisys doesn’t change zone settings',
    ddos: 'DDoS protection',
    alwaysOn: 'Always on',
    ddosHelp: 'Cloudflare’s DDoS protection is always on and can’t be turned off.',
    caching: 'Caching',
    cachingHelp:
      'Off: Nebisys adds a rule that bypasses the cache for these host names (Cloudflare caches static files by default).',
    domainsHelp: 'Host names customers use; Nebisys writes a proxied record for each into its zone.',
    edgeCertificate: 'Cloudflare’s edge certificate',
    zoneId: (i: number) => `Zone ID of domain ${i}`,
    zoneIdShort: 'Zone ID',
    zoneIdHelp: 'From the zone’s overview page in the Cloudflare dashboard (32 hex characters).',
    zoneIdInvalid: 'Enter the zone ID: 32 lowercase hex characters.',
    sslNote:
      'SSL Full (strict) is set for these host names only; your zone’s other settings stay as they are.',
    accessLabel: {
      'allow-list-and-header': 'Cloudflare ranges + secret header',
      header: 'Secret header only',
    } as Record<string, string>,
    accessSentence: {
      'allow-list-and-header':
        'Only Cloudflare’s published address ranges reach the app, and Cloudflare adds this route’s secret header.',
      header:
        'Cloud Run can’t limit callers by address: callers who know the app’s own URL reach it without the WAF unless the app checks X-Nebisys-Origin.',
    } as Record<string, string>,
    appCheck: (app: string) =>
      `${app} must check the header X-Nebisys-Origin against NEBISYS_ORIGIN_SECRET; until it does, callers who reach ${app} directly bypass the WAF.`,
    notLocked: (app: string) => `Callers can reach ${app} directly at its own URL and bypass the WAF.`,
    noOrigins: 'No web or function app to route to yet: deploy one first.',
    ecsNotAttached: (deployment: string, network: string, region: string) =>
      `Attach ${deployment} to ${network} in ${region} to route Cloudflare to it.`,
    sharedAlb: (app: string, others: string, region: string) =>
      `${others} share ${app}’s load balancer in ${region}: they also become reachable only through Cloudflare.`,
    costLine: (plan: string, currency: string, monthly: number) =>
      `Cloudflare ${plan}: ${usd(currency, monthly)}/month per zone (your Cloudflare plan)`,
    costFree: (plan: string) => `Cloudflare ${plan}: USD 0 (your Cloudflare plan)`,
    // Status
    proxiedRecord: (zone: string) => `Proxied record in zone ${zone} · created by Nebisys`,
    waitingForOrigin: 'Waiting for an origin',
    // Step 8b (spec 11 item 39): below Enterprise the origin serves the route's host names as its custom domain.
    originDomainAdding: 'Adding the domain on the origin',
    originDomainServed: 'Custom domain on the origin',
    planOriginNote:
      'Below Enterprise, Nebisys adds each route’s host names to its App Service, Function or ECS Express app as a custom domain. Cloud Run origins need Enterprise.',
    notProbedHint: 'Nebisys doesn’t health-check Cloudflare’s origins',
    cachingOff: 'Caching off',
    cachingOn: 'Caching',
    entryPointErrors: (ep: string) => `The check of entry point ${ep} found a problem.`,
    sslChip: 'SSL Full (strict) · these host names only',
    edgeCertificateLine: 'Cloudflare’s edge certificate',
    servedAfterApply: (network: string, route: string) => `Apply ${network} to serve route ${route}.`,
    // Deployment side
    lockdown: (app: string) => `${app} accepts only Cloudflare’s addresses and checks its secret header`,
    lockdownHeader: (app: string) =>
      `${app} checks Cloudflare’s secret header (it can’t limit callers by address)`,
  },
  // 6.15.29 Errors
  codes: {
    ROUTE_ORIGIN_NOT_ATTACHED: (deployment: string, network: string) =>
      `Attach ${deployment} to ${network} first`,
    ROUTE_ORIGIN_UNKNOWN: 'This app no longer exists; choose another origin',
    AZURE_APP_GATEWAY_HUB_TOO_SMALL:
      'This region’s hub is too small for a gateway: use a network with /22 hubs',
    CERTIFICATE_ACCESS_PENDING: (principal: string) =>
      `Grant Key Vault Secrets User to the gateway’s identity ${principal}, then apply the network`,
    ORIGIN_SECRET_UNAVAILABLE: (network: string) =>
      `Ask a network admin to apply ${network}; its apply creates a new origin secret`,
    ORIGIN_SECRET_REGENERATED: (route: string, deployment: string) =>
      `Requests through route ${route} fail until ${deployment} is applied`,
    ORIGIN_ROTATION_PENDING: 'A rotation of this route is still running; it finishes with the next applies.',
    ORIGIN_SECRET_NOT_USED: 'No route of this entry point uses an origin secret.',
    CONNECTION_THROUGH_EDGE: (app: string, target: string, ep: string, url: string) =>
      `${app} reaches ${target} through entry point ${ep} at ${url}`,
    ORIGIN_UNEXPECTED_PRIVATE_ENDPOINT:
      'An unexpected private endpoint connection on this app was rejected; only the network’s Front Door may connect.',
    // 6.15.34 (feature spec 17, phase 5)
    DNS_ZONE_NOT_SUPPORTED: 'Only Amazon CloudFront writes validation records into a Route 53 zone.',
    ROUTE_SHAPE_NOT_SUPPORTED: 'Amazon CloudFront routes by path only: every route serves all domains.',
    ROUTE_SHAPE_REGION: 'AWS Global Accelerator routes by region only: the path is /*.',
    AWS_VPC_ORIGIN_NEEDS_PRIVATE_SUBNETS: (app: string, network: string) =>
      `${app} can be a VPC origin only with outbound internet through NAT gateways; ${network}’s AWS egress is public subnets.`,
    AWS_EXPRESS_EXPOSURE_CHANGE: (app: string) =>
      `${app} moves behind an internal load balancer for CloudFront; its URL changes.`,
    AWS_INTERNAL_ALB_UNREACHABLE: (app: string) =>
      `${app} moves behind the region’s internal load balancer too: callers outside the VPC can’t reach it directly.`,
    AWS_IMAGE_HOST_EGRESS: (host: string) =>
      `Private tasks pull images from ${host}: add it to the allowed egress of the AWS firewall.`,
    AWS_SHIELD_ALREADY_SUBSCRIBED:
      'The AWS account already has Shield Advanced: Nebisys adds only the protections, without a new subscription.',
    AWS_SHIELD_NOT_CHECKED: 'Nebisys couldn’t check the account’s Shield Advanced subscription.',
    // 6.15.38 (feature spec 17, phase 7)
    ORIGIN_DETACH_BLOCKED: (app: string, route: string, ep: string, network: string) =>
      `${app} serves route ${route} of entry point ${ep} through the Google Cloud load balancer. Remove the route and apply ${network} first.`,
    openEntryPoint: (ep: string) => `Open entry point ${ep}`,
    GCP_ARMOR_ENTERPRISE_SUBSCRIPTION: (network: string) =>
      `Cloud Armor Enterprise Annual needs the billing account’s subscription, which a billing administrator starts in the Google Cloud console; Nebisys enrols ${network}’s projects in it. Its renewal is the billing account’s setting.`,
    ENTRY_DOMAIN_REQUIRED:
      'The load balancer has an IP address, no host name of its own: add at least one domain.',
    ADD_ON_NOT_AVAILABLE_BOT: 'Cloud Armor bot management comes later; turn bot protection off.',
    // 6.15.39 (feature spec 17, phase 8)
    DNS_ZONE_REQUIRED: (host: string) =>
      `Cloudflare needs the zone of ${host || 'this domain'} (its zone ID, from the zone’s overview page).`,
    CLOUDFLARE_ZONE_IN_USE: (zone: string, other: string) =>
      `Zone ${zone} is already used by entry point ${other}. Each Cloudflare zone’s rules belong to one entry point.`,
    CLOUDFLARE_ROUTE_SHAPE: (host: string, app: string) =>
      `Cloudflare sends ${host} to one origin. Use another host name for ${app}.`,
    CLOUDFLARE_CERTIFICATE_NOT_COVERED: (host: string) =>
      `Cloudflare’s Universal SSL certificate covers only the zone’s apex and its first-level names: ${host} needs an advanced or custom certificate in Cloudflare.`,
    CLOUDFLARE_WAF_DETECTION_NEEDS_ENTERPRISE:
      'Detection mode needs Enterprise: choose Prevention or the Enterprise plan.',
    AWS_SHARED_ALB_LOCKED: (app: string, edge: string, region: string, other: string) =>
      `${app} is only reachable through ${edge} in ${region}, because its load balancer is locked to ${edge} for ${other}’s route. Give ${app} a route, or move it to another deployment.`,
    AWS_ORIGIN_EXPOSURE_CONFLICT: (app: string, region: string, other: string) =>
      `${app}’s CloudFront VPC origin needs an internal load balancer in ${region}, and ${other}’s Cloudflare route needs it internet-facing. Put them in different deployments or regions.`,
  },
};
